import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { ModuleTips } from '../components/onboarding';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Users, Plus, Trash2, DollarSign, FileText, AlertTriangle, CheckCircle, Loader2, Mail, Calendar, Square, CheckSquare
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Contractors = () => {
  const { t } = useTranslation();
  const [contractors, setContractors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(null);
  const [selectedContractors, setSelectedContractors] = useState([]);
  const [confirmDeleteContractorId, setConfirmDeleteContractorId] = useState(null);
  const [bulkDeleteContractorsArmed, setBulkDeleteContractorsArmed] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    notes: ''
  });
  const [paymentData, setPaymentData] = useState({
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
    payment_method: 'check'
  });

  useEffect(() => {
    fetchContractors();
  }, []);

  const fetchContractors = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/contractors`, {
        withCredentials: true
      });
      // contractors.py returns a flat array with ytd_total, requires_1099_nec, payment_count
      const data = Array.isArray(response.data) ? response.data : [];
      setContractors(data);
      // Derive summary from the flat array
      setSummary({
        total_contractors: data.length,
        contractors_needing_1099: data.filter(c => c.requires_1099_nec).length,
        total_1099_payments: data.filter(c => c.requires_1099_nec).reduce((sum, c) => sum + (c.ytd_total || 0), 0)
      });
    } catch (error) {
      toast.error(t('contractors.failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateContractor = async () => {
    if (!formData.name) {
      toast.error(t('contractors.enter_name'));
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${API_URL}/api/contractors`,
        formData,
        { withCredentials: true }
      );
      toast.success(t('contractors.contractor_added'));
      setShowForm(false);
      setFormData({ name: '', email: '', address: '', notes: '' });
      fetchContractors();
    } catch (error) {
      toast.error(t('contractors.failed_to_add'));
    } finally {
      setCreating(false);
    }
  };

  const handleAddPayment = async (contractorId) => {
    if (!paymentData.amount || !paymentData.description) {
      toast.error(t('contractors.enter_amount_description'));
      return;
    }

    setCreating(true);
    try {
      await axios.post(
        `${API_URL}/api/contractors/${contractorId}/payments`,
        {
          amount: parseFloat(paymentData.amount),
          paid_date: paymentData.date,
          description: paymentData.description,
          payment_method: paymentData.payment_method
        },
        { withCredentials: true }
      );
      toast.success(t('contractors.payment_recorded'));
      setShowPaymentForm(null);
      setPaymentData({
        amount: '',
        date: new Date().toISOString().split('T')[0],
        description: '',
        payment_method: 'check'
      });
      fetchContractors();
    } catch (error) {
      toast.error(t('contractors.failed_to_record_payment'));
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (contractorId) => {
    if (confirmDeleteContractorId !== contractorId) {
      setConfirmDeleteContractorId(contractorId);
      toast.info('Click delete again to confirm', { duration: 3000 });
      setTimeout(() => setConfirmDeleteContractorId(null), 3000);
      return;
    }
    setConfirmDeleteContractorId(null);
    try {
      await axios.delete(`${API_URL}/api/contractors/${contractorId}`, {
        withCredentials: true
      });
      toast.success(t('contractors.contractor_deleted'));
      fetchContractors();
    } catch (error) {
      toast.error(t('contractors.failed_to_delete'));
    }
  };

  const toggleContractorSelect = (id) => {
    setSelectedContractors(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const handleSelectAllContractors = () => {
    setSelectedContractors(prev => prev.length === contractors.length ? [] : contractors.map(c => c.contractor_id));
  };
  const handleBulkDeleteContractors = async () => {
    if (selectedContractors.length === 0) return;
    if (!bulkDeleteContractorsArmed) {
      setBulkDeleteContractorsArmed(true);
      toast.info(`Click again to delete ${selectedContractors.length} contractors`, { duration: 3000 });
      setTimeout(() => setBulkDeleteContractorsArmed(false), 3000);
      return;
    }
    setBulkDeleteContractorsArmed(false);
    try {
      const response = await axios.post(`${API_URL}/api/contractors/bulk-delete`, selectedContractors, { withCredentials: true });
      toast.success(t('toast.deleted_docs', { count: response.data.count }));
      setSelectedContractors([]);
      fetchContractors();
    } catch (error) { toast.error(t('toast.failed_to_delete')); }
  };

  return (
  <>
    <DashboardLayout>
      <div className="space-y-6" data-testid="contractors-page">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-stone-900 font-heading">{t('contractors.title')}</h1>
            <p className="text-stone-600">{t('contractors.subtitle')}</p>
          </div>
          <Button onClick={() => setShowForm(!showForm)} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            {t('contractors.add_contractor')}
          </Button>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('contractors.total_contractors')}</p>
                    <p className="text-2xl font-bold text-stone-900 font-heading">{summary.total_contractors}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className={`bg-white rounded-2xl shadow-soft ${summary.contractors_needing_1099 > 0 ? 'border-amber-200 bg-amber-50' : 'border-stone-200'}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${summary.contractors_needing_1099 > 0 ? 'bg-amber-100' : 'bg-green-100'}`}>
                    {summary.contractors_needing_1099 > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-600" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('contractors.need_1099')}</p>
                    <p className="text-2xl font-bold text-stone-900 font-heading">{summary.contractors_needing_1099}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-sm text-stone-600">{t('contractors.total_1099_payments')}</p>
                    <p className="text-2xl font-bold text-emerald-600 font-heading">${summary.total_1099_payments.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200 rounded-2xl">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">{t('contractors.filing_requirement')}</p>
                <p>{t('contractors.filing_requirement_desc')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Add Contractor Form */}
        {showForm && (
          <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
            <CardHeader>
              <CardTitle className="font-heading">{t('contractors.add_new_contractor')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('contractors.contractor_name')} *</Label>
                  <Input
                    placeholder="John Smith Design LLC"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="contractor-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('contractors.email')}</Label>
                  <Input
                    type="email"
                    placeholder="contractor@email.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    data-testid="contractor-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>{t('contractors.address')}</Label>
                <Input
                  placeholder="123 Main St, City, State ZIP"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  data-testid="contractor-address-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('contractors.notes')}</Label>
                <Input
                  placeholder={t('contractors.services_provided')}
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  data-testid="contractor-notes-input"
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateContractor} disabled={creating} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-contractor-btn">
                  {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                  {t('contractors.add_contractor')}
                </Button>
                <Button variant="outline" onClick={() => setShowForm(false)}>{t('common.cancel')}</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contractors List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-stone-400" />
              </CardContent>
            </Card>
          ) : contractors.length === 0 ? (
            <Card className="bg-white rounded-2xl border border-stone-200 shadow-soft">
              <CardContent className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">{t('contractors.no_contractors')}</p>
                <p className="text-sm text-stone-400">{t('contractors.no_contractors_desc')}</p>
              </CardContent>
            </Card>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <Button variant="outline" size="sm" onClick={handleSelectAllContractors} data-testid="select-all-contractors">
                  {selectedContractors.length === contractors.length && contractors.length > 0 ? <><CheckSquare className="w-4 h-4 mr-1" /> Deselect All</> : <><Square className="w-4 h-4 mr-1" /> Select All</>}
                </Button>
                {selectedContractors.length > 0 && (
                  <Button variant="destructive" size="sm" onClick={handleBulkDeleteContractors} data-testid="bulk-delete-contractors">
                    <Trash2 className="w-4 h-4 mr-1" /> Delete {selectedContractors.length} Selected
                  </Button>
                )}
              </div>
            {contractors.map((contractor) => (
              <Card key={contractor.contractor_id} className={`bg-white rounded-2xl shadow-soft ${selectedContractors.includes(contractor.contractor_id) ? 'ring-2 ring-primary' : ''} ${contractor.requires_1099_nec ? 'border-amber-200' : 'border-stone-200'}`} data-testid={`contractor-${contractor.contractor_id}`}>
                <CardContent className="py-3 sm:py-4">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <Checkbox checked={selectedContractors.includes(contractor.contractor_id)} onCheckedChange={() => toggleContractorSelect(contractor.contractor_id)} className="mt-1 flex-shrink-0" />
                      <div className={`w-10 h-10 sm:w-12 sm:h-12 flex-shrink-0 rounded-full flex items-center justify-center ${contractor.requires_1099_nec ? 'bg-amber-100' : 'bg-stone-100'}`}>
                        <Users className={`w-5 h-5 sm:w-6 sm:h-6 ${contractor.requires_1099_nec ? 'text-amber-600' : 'text-stone-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-sm sm:text-base text-stone-900">{contractor.name}</h3>
                          {contractor.requires_1099_nec && (
                            <Badge className="bg-amber-100 text-amber-800 border-amber-200 text-xs">{t('contractors.1099_required')}</Badge>
                          )}
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-stone-500 mt-1">
                          {contractor.email && (
                            <span className="flex items-center gap-1 truncate">
                              <Mail className="w-3 h-3 flex-shrink-0" />
                              <span className="truncate">{contractor.email}</span>
                            </span>
                          )}
                          <span>{contractor.payment_count || 0} {t('contractors.payments')}</span>
                        </div>
                        {contractor.notes && (
                          <p className="text-xs sm:text-sm text-stone-500 mt-1 line-clamp-2">{contractor.notes}</p>
                        )}
                        {/* Mobile: Show amount below */}
                        <div className="mt-2 sm:hidden">
                          <p className="text-xl font-bold text-stone-900">${(contractor.ytd_total || 0).toLocaleString()}</p>
                          <p className="text-xs text-stone-500">{t('contractors.ytd_payments')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-14 sm:pl-0">
                      {/* Desktop: Show amount */}
                      <div className="hidden sm:block text-right">
                        <p className="text-2xl font-bold text-stone-900 font-heading">${(contractor.ytd_total || 0).toLocaleString()}</p>
                        <p className="text-sm text-stone-500">{t('contractors.ytd_payments')}</p>
                      </div>
                      <div className="flex items-center gap-1 sm:flex-col sm:gap-1">
                        <Button
                          size="sm"
                          onClick={() => setShowPaymentForm(showPaymentForm === contractor.contractor_id ? null : contractor.contractor_id)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-xs"
                          data-testid={`add-payment-${contractor.contractor_id}`}
                        >
                          <Plus className="w-3 h-3 sm:mr-1" />
                          <span className="hidden sm:inline">{t('contractors.add_payment')}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(contractor.contractor_id)}
                          data-testid={`delete-contractor-${contractor.contractor_id}`}
                          className="h-8 w-8"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Payment Form */}
                  {showPaymentForm === contractor.contractor_id && (
                    <div className="mt-4 pt-4 border-t border-stone-200">
                      <div className="grid md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                          <Label>{t('contractors.amount')}</Label>
                          <Input
                            type="number"
                            placeholder="500.00"
                            value={paymentData.amount}
                            onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                            data-testid="payment-amount-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>{t('contractors.payment_date')}</Label>
                          <Input
                            type="date"
                            value={paymentData.date}
                            onChange={(e) => setPaymentData({ ...paymentData, date: e.target.value })}
                            data-testid="payment-date-input"
                          />
                        </div>
                        <div className="space-y-2 md:col-span-2">
                          <Label>{t('contractors.description')}</Label>
                          <Input
                            placeholder={t('contractors.description_placeholder')}
                            value={paymentData.description}
                            onChange={(e) => setPaymentData({ ...paymentData, description: e.target.value })}
                            data-testid="payment-description-input"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button 
                          onClick={() => handleAddPayment(contractor.contractor_id)} 
                          disabled={creating}
                          className="bg-emerald-600 hover:bg-emerald-700"
                          data-testid="record-payment-btn"
                        >
                          {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <DollarSign className="w-4 h-4 mr-2" />}
                          {t('contractors.record_payment')}
                        </Button>
                        <Button variant="outline" onClick={() => setShowPaymentForm(null)}>{t('common.cancel')}</Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
    <ModuleTips moduleId="contractors" />
  </>
  );
};

export default Contractors;
